// src/layouts/Pages/PageHeader/dropdownMenus.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  InboxOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import appService from 'services/appService';
import routeService from 'services/routeService';
import { cancelAllRequests } from 'configs/fetchInterceptor';
import translateX from 'utils/translateX';
import { useModuleStore } from 'stores/module';
import { PiLifebuoy } from 'react-icons/pi';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const divider = { type: 'divider' };

// ─────────────────────────────────────────────────────────────────────────────
// NEW DOCUMENT
// Navigates to /add and resets the form (same behavior as exiting and clicking New)
// Always enabled
// ─────────────────────────────────────────────────────────────────────────────
const newDocument = {
  key: 'new',
  label: 'new_document',
  icon: <PlusOutlined />,
  onClick: ({ resetForm, setMode, setPageData, apiGlobal }) => {
    // Cancel any pending API requests to prevent race conditions
    cancelAllRequests();

    appService.message({ flag: 'info', text: 'new_document' });
    routeService.updateUrlToAdd();

    // Reset internal API state (_id, documentId) to ensure POST instead of PUT
    apiGlobal?.resetForNewDocument?.();

    resetForm?.();
    setMode?.('add');
    setPageData?.({ data: null, summary: null, force: true });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// EDIT
// Same behavior as EditButton - enables editing
// Disabled when no document is loaded (no _id)
// ─────────────────────────────────────────────────────────────────────────────
const edit = {
  key: 'edit',
  label: 'edit',
  icon: <EditOutlined />,
  disabled: ({ currentPage }) => !currentPage?.data?._id,
  onClick: ({ setMode }) => {
    appService.message({ flag: 'info', text: 'edit_mode_activated' });
    setMode?.('edit');
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// RELOAD
// Same behavior as RefreshButton - reloads and returns to view
// Disabled when no document is loaded (no _id)
// ─────────────────────────────────────────────────────────────────────────────
const reload = {
  key: 'reload',
  label: 'reload_document',
  icon: <ReloadOutlined />,
  disabled: ({ currentPage }) => !currentPage?.data?._id,
  onClick: ({ apiGlobal, handleListRefresh, setMode, page }) => {
    appService.message({ flag: 'info', text: 'document_reloaded' });

    // For list pages, use the handleListRefresh function from ModuleContext (registered by ListProvider)
    if (typeof handleListRefresh === 'function') {
      handleListRefresh();
      return;
    }

    // For document pages, use the global API refresh
    const _id = page?.data?._id;
    apiGlobal?.refresh?.({ _id, showLoading: true });
    setMode?.('view');
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// DUPLICATE
// Changes URL to /add, clears storePageData, but does NOT reset the form
// Keeps current record data to save as new
// Disabled when no document is loaded (no _id)
// ─────────────────────────────────────────────────────────────────────────────
const duplicate = {
  key: 'duplicate',
  label: 'duplicate_document',
  icon: <CopyOutlined />,
  disabled: ({ currentPage }) => !currentPage?.data?._id,
  onClick: ({ setMode, setPageData, page, moduleContext, apiGlobal }) => {
    // Cancel any pending API requests to prevent race conditions
    cancelAllRequests();

    // appService.message({ flag: 'success', text: 'document_cloned_successfully' });

    // IMPORTANT: Prepare data FIRST (before any navigation/state changes)
    // This ensures the store has the correct data before any component remount
    const currentData = page?.data;
    if (!currentData) return; // Can't duplicate without data

    // Create a copy of data without _id so that saving will POST (create new) instead of PUT
    // Keep other fields like backColor, foreColor, etc. so avatar colors are preserved
    // NOTE: Attachment referenceCount is handled by backend on document save
    const { _id, _metadata, ...dataWithoutId } = currentData;

    // IMPORTANT: Set documentDuplicated flag using STORE (not context)
    // This avoids stale closure issues when context doesn't propagate correctly
    // done=false sets documentDuplicated to truthy value (+new Date())
    const { setDocumentDuplicatedStore } = useModuleStore.getState();
    setDocumentDuplicatedStore?.(false);

    // Update store data BEFORE URL change to avoid race conditions
    // If component remounts due to URL change, it will see the updated store data
    setPageData?.({ data: dataWithoutId, summary: null, force: true });

    // Update URL to /add using replace (doesn't add to browser history)
    routeService.updateUrlToAdd();

    // Change mode to 'add' - form keeps current data for duplication
    setMode?.('add');

    // Reset internal API state (_id, documentId) to ensure POST instead of PUT
    apiGlobal?.resetForNewDocument?.();

    // Clear _id and _metadata from the form to ensure POST instead of PUT
    const form = moduleContext?.formContext?.form;
    if (form) {
      form.setFieldsValue({ _id: undefined, _metadata: undefined });
    }

    appService.notification({
      flag: 'info',
      title: 'document_duplicated',
      description: 'document_duplicated_text',
      duration: 15,
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// ARCHIVE
// Hidden only when document is already removed (in trash)
// Disabled when no _id
// ─────────────────────────────────────────────────────────────────────────────
const archive = {
  key: 'archive',
  label: 'archive_document',
  icon: <InboxOutlined />,
  hidden: ({ currentPage }) => !!currentPage?.data?._metadata?.audit?.removedAt,
  disabled: ({ currentPage }) => !currentPage?.data?._id,
  onClick: ({ handleArchiveDocuments, page, handleListRefresh }) => {
    const data = page?.data;
    if (!data?._id) return;

    // Call the archive handler from ModuleContext
    // Pass the current document as an array and refresh callback
    handleArchiveDocuments?.([data], {
      onSuccess: handleListRefresh,
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// REMOVE
// Hidden only when document is already removed (in trash)
// Disabled when no _id
// ─────────────────────────────────────────────────────────────────────────────
const remove = {
  key: 'remove',
  label: 'remove',
  icon: <DeleteOutlined />,
  hidden: ({ currentPage }) => !!currentPage?.data?._metadata?.audit?.removedAt,
  disabled: ({ currentPage }) => !currentPage?.data?._id,
  danger: true,
  onClick: ({ handleRemoveDocuments, page, handleListRefresh }) => {
    const data = page?.data;
    if (!data?._id) return;

    // Call the remove handler from ModuleContext
    // Pass the current document as an array and refresh callback
    handleRemoveDocuments?.([data], {
      onSuccess: handleListRefresh,
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// RESTORE
// Hidden when document is NOT in trash or archived
// Disabled when no _id
// ─────────────────────────────────────────────────────────────────────────────
const restore = {
  key: 'restore',
  label: 'restore',
  icon: <PiLifebuoy />,
  hidden: ({ currentPage }) =>
    !currentPage?.data?._metadata?.audit?.removedAt &&
    !currentPage?.data?._metadata?.audit?.filedAt,
  disabled: ({ currentPage }) => !currentPage?.data?._id,
  onClick: ({ handleRestoreDocuments, page, handleListRefresh }) => {
    const data = page?.data;
    if (!data?._id) return;

    // Call the restore handler from ModuleContext
    // Pass the current document as an array and refresh callback
    handleRestoreDocuments?.([data], {
      onSuccess: handleListRefresh,
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// REMOVE PERMANENTLY
// Only visible when document is in trash (has removedAt)
// Hidden when no _id or when document is NOT removed
// ─────────────────────────────────────────────────────────────────────────────
const removePermanently = {
  key: 'removePermanently',
  label: 'remove_permanently',
  icon: <DeleteOutlined />,
  danger: true,
  hidden: ({ currentPage }) => {
    const data = currentPage?.data;
    const hasId = !!data?._id;
    const isRemoved = !!data?._metadata?.audit?.removedAt;
    return !hasId || !isRemoved;
  },
  disabled: ({ currentPage }) => !currentPage?.data?._id,
  onClick: ({ handleRemoveDocuments, page, handleListRefresh }) => {
    const data = page?.data;
    if (!data?._id) return;

    // Call the remove handler from ModuleContext
    // When document is in recycle folder, this will permanently remove it
    handleRemoveDocuments?.([data], {
      onSuccess: handleListRefresh,
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// PARAMETERS
// Opens the module parameters modal
// Reusable across modules - uses setShowParametersModal from ModuleContext
// Always enabled (parameters can be configured regardless of document state)
// ─────────────────────────────────────────────────────────────────────────────
const parameters = {
  key: 'parameters',
  label: 'parameters',
  icon: <SettingOutlined />,
  onClick: ({ moduleContext }) => {
    const setShowParametersModal = moduleContext?.setShowParametersModal;
    if (typeof setShowParametersModal === 'function') {
      setShowParametersModal(true);
    } else {
      console.warn('[dropdownMenus] setShowParametersModal not available in ModuleContext');
    }
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export {
  divider as dropdownDivider,
  newDocument as dropdownNew,
  edit as dropdownEdit,
  reload as dropdownReload,
  duplicate as dropdownDuplicate,
  archive as dropdownArchive,
  remove as dropdownRemove,
  restore as dropdownRestore,
  removePermanently as dropdownRemovePermanently,
  parameters as dropdownParameters,
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
